/**
 * The snapshot tree is derived, not stored.
 *
 * Snapshots live in one flat folder on disk; their grouping comes from each
 * file's own `metadata.exportFolder` — the same field the export step already
 * uses to decide which delivery folder a playable belongs in. So there is no
 * hierarchy document to keep in sync: moving a snapshot means writing a new
 * `exportFolder` into it, and the tree follows.
 *
 * A path may nest ("brand/batch"); segments are trimmed and empty ones dropped,
 * so "  " and null both mean root.
 *
 * One consequence of deriving the tree: an empty folder cannot exist on disk,
 * because a folder only exists as a value some file holds. "Create folder" is
 * therefore a client-side draft — a place to drop the first snapshot into, which
 * is the moment the folder becomes real. See `draftFolderPaths` below.
 */

export const ROOT_ID = 0;

/** Node id for a folder path — stable across renders so open/closed state sticks. */
export function folderNodeId(folderPath) {
  return `folder:${folderPath}`;
}

/** Node id for a snapshot file. */
export function fileNodeId(fileName) {
  return `file:${fileName}`;
}

/** Normalize an exportFolder value to a clean "a/b" path, or "" for root. */
export function normalizeFolderPath(value) {
  return String(value == null ? "" : value)
    .split("/")
    .map((segment) => segment.trim())
    .filter(Boolean)
    .join("/");
}

/** The folder path a node id belongs to: "" for root, "a/b" for a folder node. */
export function folderPathFromNodeId(nodeId) {
  if (nodeId === ROOT_ID || nodeId === String(ROOT_ID)) return "";
  const raw = String(nodeId);
  return raw.startsWith("folder:") ? raw.slice("folder:".length) : null;
}

/** The file name a node id refers to, or null when it is not a file node. */
export function fileNameFromNodeId(nodeId) {
  const raw = String(nodeId);
  return raw.startsWith("file:") ? raw.slice("file:".length) : null;
}

/**
 * Compare two tree nodes for sibling order. Folders stay before files under the
 * same parent; within each group we sort by name or by Updated (mtime).
 *
 * @param {"name"|"updated"} sortBy
 * @param {"asc"|"desc"} sortDir
 */
function compareNodes(a, b, sortBy, sortDir) {
  const dir = sortDir === "desc" ? -1 : 1;

  if (sortBy === "updated") {
    const aTime = typeof a.data?.mtimeMs === "number" ? a.data.mtimeMs : null;
    const bTime = typeof b.data?.mtimeMs === "number" ? b.data.mtimeMs : null;
    if (aTime == null && bTime == null) {
      return a.text.localeCompare(b.text) * dir;
    }
    if (aTime == null) return 1;
    if (bTime == null) return -1;
    if (aTime !== bTime) return (aTime - bTime) * dir;
    return a.text.localeCompare(b.text);
  }

  return a.text.localeCompare(b.text) * dir;
}

/**
 * Everything one snapshot can be found by, as one lowercased string.
 *
 * The file name is what the row shows, but it is rarely what someone has in
 * mind: the folder it sits in, the name it is delivered under and the task it
 * belongs to are all on screen somewhere else, and typing one of them used to
 * find nothing. The folder path is in here rather than matched on the folder
 * node, because a folder is worth finding for what is inside it — matching its
 * files keeps the folder and its contents together.
 */
function searchableText(item) {
  return [
    item.fileName,
    normalizeFolderPath(item.exportFolder),
    item.exportName,
    item.taskName,
    item.assignee,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

/**
 * Build the flat node list @minoru/react-dnd-treeview expects.
 *
 * Every ancestor folder is emitted even when nothing sits directly in it, so
 * "brand/batch" produces both "brand" and "brand/batch".
 *
 * @param {Array} snapshots  listSnapshots summaries
 * @param {string} [filterTerm]  case-insensitive filter over everything a
 *   snapshot is found by (see `searchableText`); folders with no surviving
 *   child are dropped, which is also how a folder name finds its contents
 * @param {Array<string>} [draftFolderPaths]  folders the user just created that
 *   no file lives in yet. They survive the filter — filtering down to the one
 *   snapshot you want to file away and then dragging it into a new folder is the
 *   reason to have drafts at all — and are marked `data.isDraft`.
 * @param {{ sortBy?: "name"|"updated", sortDir?: "asc"|"desc" }} [sort]
 */
export function buildSnapshotTree(
  snapshots,
  filterTerm = "",
  draftFolderPaths = [],
  sort = {}
) {
  const sortBy = sort.sortBy === "updated" ? "updated" : "name";
  const sortDir = sort.sortDir === "desc" ? "desc" : "asc";

  const term = String(filterTerm || "").trim().toLowerCase();
  const matching = term
    ? (snapshots || []).filter((item) => searchableText(item).includes(term))
    : snapshots || [];

  const occupiedPaths = new Set();
  // Latest file mtime under each folder path (including nested children) — folders
  // are virtual, so "Updated" is derived from the snapshots they contain.
  const folderMtimeMs = new Map();
  // How many snapshots sit under each folder path and how many of those are
  // locked. A folder has no lock of its own — it reads as locked exactly when
  // it holds something and every one of them is locked.
  const folderCounts = new Map();
  const fileNodes = [];

  for (const item of matching) {
    const folderPath = normalizeFolderPath(item.exportFolder);
    const itemMtime = typeof item.mtimeMs === "number" ? item.mtimeMs : null;

    if (folderPath) {
      const segments = folderPath.split("/");
      for (let i = 1; i <= segments.length; i++) {
        const ancestor = segments.slice(0, i).join("/");
        occupiedPaths.add(ancestor);
        const counts = folderCounts.get(ancestor) || { total: 0, locked: 0 };
        counts.total += 1;
        if (item.locked === true) counts.locked += 1;
        folderCounts.set(ancestor, counts);
        if (itemMtime != null) {
          const prev = folderMtimeMs.get(ancestor);
          if (prev == null || itemMtime > prev) folderMtimeMs.set(ancestor, itemMtime);
        }
      }
    }

    fileNodes.push({
      id: fileNodeId(item.fileName),
      parent: folderPath ? folderNodeId(folderPath) : ROOT_ID,
      droppable: false,
      text: String(item.fileName).replace(/\.json$/i, ""),
      data: item,
    });
  }

  const folderPaths = new Set(occupiedPaths);
  for (const rawPath of draftFolderPaths || []) {
    const folderPath = normalizeFolderPath(rawPath);
    if (!folderPath) continue;
    const segments = folderPath.split("/");
    for (let i = 1; i <= segments.length; i++) {
      folderPaths.add(segments.slice(0, i).join("/"));
    }
  }

  const folderNodes = Array.from(folderPaths).map((folderPath) => {
    const segments = folderPath.split("/");
    const parentPath = segments.slice(0, -1).join("/");
    return {
      id: folderNodeId(folderPath),
      parent: parentPath ? folderNodeId(parentPath) : ROOT_ID,
      droppable: true,
      text: segments[segments.length - 1],
      data: {
        isFolder: true,
        folderPath,
        isDraft: !occupiedPaths.has(folderPath),
        mtimeMs: folderMtimeMs.get(folderPath) ?? null,
        // Derived, never stored: an empty folder is not "locked", it is empty.
        locked: (folderCounts.get(folderPath)?.total || 0) > 0 &&
          folderCounts.get(folderPath).locked === folderCounts.get(folderPath).total,
      },
    };
  });

  // Folders before files under each parent; order within each group follows sort.
  // The treeview keeps sibling order from this flat list.
  const bySibling = (a, b) => compareNodes(a, b, sortBy, sortDir);
  folderNodes.sort(bySibling);
  fileNodes.sort(bySibling);
  return [...folderNodes, ...fileNodes];
}

/** True when `folderPath` is `ancestor` itself or sits inside it. */
export function isInsideFolder(folderPath, ancestor) {
  return folderPath === ancestor || folderPath.startsWith(`${ancestor}/`);
}

/**
 * Rewrite one path for a folder rename, the way the server rewrites each file's
 * `exportFolder`: "a/b" under "a" → "z" becomes "z/b", and an empty `to` lifts
 * it to the root. Paths outside the renamed folder come back untouched.
 */
export function applyFolderRename(folderPath, from, to) {
  if (!isInsideFolder(folderPath, from)) return folderPath;
  const suffix = folderPath.slice(from.length); // "" or "/b"
  return to ? `${to}${suffix}` : suffix.replace(/^\//, "");
}

/** Every folder path currently in use, sorted — for "move to folder" pickers. */
export function collectFolderPaths(snapshots) {
  const paths = new Set();
  for (const item of snapshots || []) {
    const folderPath = normalizeFolderPath(item.exportFolder);
    if (!folderPath) continue;
    const segments = folderPath.split("/");
    for (let i = 1; i <= segments.length; i++) {
      paths.add(segments.slice(0, i).join("/"));
    }
  }
  return Array.from(paths).sort();
}
